import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import adminService, { type UsageEventItem } from '../services/adminService';
import type { User } from '../types/auth';

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  return u.is_superuser === true || u.role === 'admin';
}

function formatDt(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const PAGE = 100;

export default function AdminUsagePage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [items, setItems] = useState<UsageEventItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [toolKeys, setToolKeys] = useState<string[]>([]);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [toolKey, setToolKey] = useState('');
  const [isGuest, setIsGuest] = useState<string>(''); // '' | 'true' | 'false'
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const loadToolKeys = useCallback(async () => {
    try {
      const k = await adminService.listUsageToolKeys();
      setToolKeys(k);
    } catch {
      setToolKeys([]);
    }
  }, []);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const data = await adminService.listUsageEvents({
        since: since || undefined,
        until: until || undefined,
        tool_key: toolKey || undefined,
        is_guest: isGuest === '' ? undefined : isGuest === 'true',
        skip,
        limit: PAGE,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to load usage';
      setErr(String(msg));
    } finally {
      setLoading(false);
    }
  }, [since, until, toolKey, isGuest, skip]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdminUser(me)) return;
    void loadToolKeys();
  }, [authLoading, me, loadToolKeys]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdminUser(me)) return;
    void load();
  }, [authLoading, me, load]);

  useEffect(() => {
    if (authLoading) return;
    if (me && !isAdminUser(me)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, me, navigate]);

  const maxPage = useMemo(
    () => Math.max(0, Math.ceil(total / PAGE) - 1),
    [total]
  );
  const pageIndex = useMemo(() => Math.floor(skip / PAGE), [skip]);

  if (authLoading || (me && !isAdminUser(me))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="Tool usage & time">
      <p className="text-sm text-gray-600 mb-4">
        Rows from <code className="text-xs bg-gray-200 px-1 rounded">compute_usage_events</code>: which
        user (or guest), which <strong>tool_key</strong>, how long the request took, and when. Data is
        produced when the API records usage; filters help you audit and plan budgets.
      </p>
      {err && (
        <div className="mb-3 p-3 rounded-lg bg-amber-50 text-amber-900 border text-sm">{err}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600">From date</label>
          <input
            type="date"
            value={since}
            onChange={(e) => {
              setSince(e.target.value);
              setSkip(0);
            }}
            className="mt-1 border rounded-md px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">To date</label>
          <input
            type="date"
            value={until}
            onChange={(e) => {
              setUntil(e.target.value);
              setSkip(0);
            }}
            className="mt-1 border rounded-md px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">Tool</label>
          <select
            value={toolKey}
            onChange={(e) => {
              setToolKey(e.target.value);
              setSkip(0);
            }}
            className="mt-1 border rounded-md px-2 py-1.5 text-sm min-w-[160px]"
          >
            <option value="">Any</option>
            {toolKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600">Session</label>
          <select
            value={isGuest}
            onChange={(e) => {
              setIsGuest(e.target.value);
              setSkip(0);
            }}
            className="mt-1 border rounded-md px-2 py-1.5 text-sm"
          >
            <option value="">Any</option>
            <option value="true">Guest</option>
            <option value="false">Signed-in user</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
        >
          Apply
        </button>
      </div>

      <div className="text-sm text-gray-500 mb-2">
        {total} event{total === 1 ? '' : 's'}
        {total > PAGE
          ? ` (showing ${skip + 1}–${Math.min(skip + PAGE, total)})`
          : ''}
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white shadow text-sm">
          <table className="min-w-full text-left">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 font-medium">Time</th>
                <th className="p-2 font-medium">Email / guest</th>
                <th className="p-2 font-medium">Tool</th>
                <th className="p-2 font-medium">ms</th>
                <th className="p-2 font-medium">HTTP</th>
                <th className="p-2 font-medium max-w-[200px]">Path</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="p-2 whitespace-nowrap text-xs">{formatDt(row.created_at)}</td>
                  <td className="p-2">
                    {row.is_guest ? (
                      <span className="text-amber-800">Guest</span>
                    ) : (
                      <span className="break-all">{row.user_email || row.user_id}</span>
                    )}
                  </td>
                  <td className="p-2 font-mono text-xs">{row.tool_key}</td>
                  <td className="p-2 tabular-nums">{row.duration_ms}</td>
                  <td className="p-2">{row.response_status ?? '—'}</td>
                  <td className="p-2 text-xs break-all text-gray-600 max-w-[240px]">
                    {row.request_path}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="p-4 text-gray-500">No events match.</p>}
        </div>
      )}

      {total > PAGE && (
        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            disabled={skip < PAGE}
            onClick={() => setSkip((s) => Math.max(0, s - PAGE))}
            className="px-3 py-1.5 rounded border text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {pageIndex + 1} of {maxPage + 1}
          </span>
          <button
            type="button"
            disabled={skip + PAGE >= total}
            onClick={() => setSkip((s) => s + PAGE)}
            className="px-3 py-1.5 rounded border text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
